import { useState } from "react";
import { HrefLink as Link } from "@/components/ui/href-link";
import { buttonVariants } from "@/components/ui/button";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { Paperclip, Plus } from "lucide-react";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { RowCheckbox } from "@/components/dashboard/data-table/selection";
import { LeaveBulkActions } from "@/components/leave/bulk-actions";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { FileLeavePanel } from "@/pages/dashboard/leave/file-panel";
import { useLocale } from "@/context/locale-provider";
import { localizedName } from "@/utils/locale";
import {
  filters,
  leave as copy,
  leaveGroups,
  nav,
  status,
} from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import {
  buildQuery,
  readEnum,
  readNumber,
  readPage,
  readPageSize,
  readSearch,
  readSort,
  emptyPage,
} from "@/utils/query-params";
import {
  LEAVE_GROUPS,
  LEAVE_SORT_COLUMNS,
  LEAVE_STATUSES,
  leaveStatusCounts,
  listLeaveGuards,
  listLeaveRequests,
  listLeaveTypes,
} from "@/api/leave";
import { instantAt, isValidDateKey } from "@/utils/time";
import { cn } from "@/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/leave";

export const Route = createFileRoute("/_main/dashboard/leave/")({
  component: () => (
    <RequirePermission action="leave.read">
      <LeavePage />
    </RequirePermission>
  ),
});

function LeavePage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const { locale } = useLocale();
  const [filing, setFiling] = useState(false);
  const params = useQueryParams();
  const fmt = useFmt();
  const scope = scopeToSupervisorId(user);

  const sort = readSort(params, LEAVE_SORT_COLUMNS, "createdAt");
  const activeStatus = readEnum(params, "status", LEAVE_STATUSES);
  const group = readEnum(params, "group", LEAVE_GROUPS);

  const leaveTypes = useQuery({
    queryKey: ["leaveTypes", "list"],
    queryFn: listLeaveTypes,
    staleTime: 30 * 60 * 1000,
  });

  const query = {
    search: readSearch(params),
    status: activeStatus,
    group,
    leaveTypeId: readNumber(params, "leaveType"),
    guardId: readNumber(params, "guard"),
    from: typeof params.from === "string" ? params.from : undefined,
    to: typeof params.to === "string" ? params.to : undefined,
    scopeToSupervisorId: scope,
    sort: { column: sort.column, direction: sort.direction },
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["leave", "list", query],
    queryFn: () => listLeaveRequests(query),
    placeholderData: keepPreviousData,
  });

  const countsQuery = useQuery({
    queryKey: ["leave", "counts"],
    queryFn: leaveStatusCounts,
  });
  const guardsQuery = useQuery({
    queryKey: ["leave", "guards"],
    queryFn: listLeaveGuards,
    staleTime: 5 * 60_000,
  });

  const result = list.data ?? emptyPage<never>();
  const counts = countsQuery.data ?? { pending: 0, approved: 0, rejected: 0 };
  const guards = guardsQuery.data ?? [];

  const rawFrom = typeof params.from === "string" ? params.from : undefined;
  const rawTo = typeof params.to === "string" ? params.to : undefined;
  const appliedFrom = rawFrom && isValidDateKey(rawFrom) ? rawFrom : undefined;
  const appliedTo = rawTo && isValidDateKey(rawTo) ? rawTo : undefined;

  const canDecide = can(user.role, "leave.decide");
  const canFile = can(user.role, "leave.create");
  const isFiltered = Boolean(
    readSearch(params) || params.group || params.leaveType || params.guard || params.from || params.to,
  );

  const selectable = result.rows
    .filter((row) => row.status === "pending" && !row.leaveType.requiresAttachment)
    .map((row) => row.id);
  const attachmentRequired = result.rows
    .filter((row) => row.status === "pending" && row.leaveType.requiresAttachment)
    .map((row) => ({
      id: row.id,
      guardName: row.guardName,
      leaveTypeName: localizedName(row.leaveType.nameAr, row.leaveType.nameEn, locale),
    }));
  const columnCount = 6 + (canDecide ? 1 : 0);

  const tabs = [
    { value: undefined, label: copy.tabAll, count: counts.pending + counts.approved + counts.rejected },
    { value: "pending" as const, label: copy.tabPending, count: counts.pending },
    { value: "approved" as const, label: copy.tabApproved, count: counts.approved },
    { value: "rejected" as const, label: copy.tabRejected, count: counts.rejected },
  ];

  const table = (
    <TableShell className="mt-5" busy={list.isPlaceholderData}>
      <Table>
        <THead>
          {canDecide ? <TH srOnly>{copy.selectAll}</TH> : null}
          <SortHeader
            column="guard"
            label={copy.columnGuard}
            params={params}
            basePath={BASE_PATH}
            current={sort}
          />
          <TH hideBelow="lg">{copy.columnType}</TH>
          <SortHeader
            column="startAt"
            label={copy.columnPeriod}
            params={params}
            basePath={BASE_PATH}
            current={sort}
          />
          <SortHeader
            column="createdAt"
            label={copy.columnFiled}
            params={params}
            basePath={BASE_PATH}
            current={sort}
          />
          <SortHeader
            column="status"
            label={copy.columnStatus}
            params={params}
            basePath={BASE_PATH}
            current={sort}
          />
          <TH numeric hideBelow="xl">
            {copy.attachment}
          </TH>
        </THead>

        <TBody>
          {result.rows.length === 0 ? (
            <TEmpty colSpan={columnCount}>
              <EmptyState
                variant={isFiltered ? "filtered" : "empty"}
                title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                actionHref={isFiltered ? BASE_PATH : undefined}
                actionLabel={isFiltered ? copy.clearFilters : undefined}
              />
            </TEmpty>
          ) : (
            result.rows.map((row) => (
              <TR key={row.id}>
                {canDecide ? (
                  <TD className="w-10">
                    <RowCheckbox
                      id={row.id}
                      label={row.guardName}
                      disabled={row.status !== "pending"}
                    />
                  </TD>
                ) : null}

                <TD>
                  <div className="flex items-center gap-2.5">
                    <PersonAvatar name={row.guardName} photoUrl={row.photoUrl} />
                    <div className="min-w-0">
                      <Link
                        to={`${BASE_PATH}/${row.id}`}
                        className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                      >
                        {row.guardName}
                      </Link>
                      {row.supervisorName ? (
                        <span className="mt-0.5 block text-xs text-n6">{row.supervisorName}</span>
                      ) : null}
                    </div>
                  </div>
                </TD>

                <TD hideBelow="lg">
                  <span className="text-xs font-semibold text-n7">
                    {localizedName(row.leaveType.nameAr, row.leaveType.nameEn, locale)}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-n6">
                    {leaveGroups[row.leaveType.group]}
                  </span>
                </TD>

                <TD>
                  <span className="tabular text-xs">{fmt.range(row.startAt, row.endAt)}</span>
                  {row.fromNow ? (
                    <span className="mt-0.5 block text-[11px] font-semibold text-orange-700">
                      {copy.fromNow}
                    </span>
                  ) : null}
                </TD>

                <TD numeric hideBelow="xl">
                  <span className="text-xs">{fmt.dateNumeric(row.createdAt)}</span>
                </TD>

                <TD>
                  {row.status === "pending" ? (
                    <StatusPill tone="attention">{status.pending}</StatusPill>
                  ) : row.status === "approved" ? (
                    <StatusPill tone="positive">{status.approved}</StatusPill>
                  ) : (
                    <StatusPill tone="negative">{status.rejected}</StatusPill>
                  )}
                  {row.reopenedAt ? (
                    <span className="mt-1 block text-[10px] font-semibold text-n6">
                      {copy.timelineReopened}
                    </span>
                  ) : null}
                </TD>

                <TD numeric hideBelow="xl">
                  {row.hasAttachment ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-n7"
                      title={copy.hasAttachment}
                    >
                      <Paperclip aria-hidden className="size-3.5" strokeWidth={1.6} />
                      <span className="sr-only">{copy.hasAttachment}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-n5" title={copy.noAttachment}>
                      —
                    </span>
                  )}
                </TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>

      {result.rows.length > 0 ? (
        <Pagination
          page={result.page}
          pageCount={result.pageCount}
          pageSize={result.pageSize}
          total={result.total}
          params={params}
          basePath={BASE_PATH}
          numerals={user.numeralSystem}
        />
      ) : null}
    </TableShell>
  );

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={copy.title}
        lead={copy.lead}
        actions={
          canFile ? (
            <button
              type="button"
              onClick={() => setFiling(true)}
              className={buttonVariants({ variant: "brand", size: "xl" })}
            >
              <Plus aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.fileOpen}
            </button>
          ) : null
        }
      />

      {filing ? <FileLeavePanel onClose={() => setFiling(false)} /> : null}

      {!canDecide ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((tab) => {
          const active = activeStatus === tab.value;
          return (
            <Link
              key={tab.label}
              to={`${BASE_PATH}${buildQuery(params, { status: tab.value ?? null })}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                active ? "border-orange text-n9" : "border-transparent text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              <span className="tabular text-[11px] text-n6">{fmt.count(tab.count)}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-5">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "group",
              label: copy.filterGroup,
              value: typeof params.group === "string" ? params.group : "",
              options: LEAVE_GROUPS.map((value) => ({
                value,
                label: leaveGroups[value],
              })),
            },
            {
              key: "leaveType",
              label: copy.filterLeaveType,
              value: typeof params.leaveType === "string" ? params.leaveType : "",
              options: (leaveTypes.data ?? [])
                .filter((type) => !group || type.group === group)
                .map((type) => ({
                  value: String(type.id),
                  label: localizedName(type.nameAr, type.nameEn, locale),
                })),
            },
            {
              key: "guard",
              label: copy.filterGuard,
              value: typeof params.guard === "string" ? params.guard : "",
              options: guards.map((guard) => ({ value: String(guard.id), label: guard.name })),
            },
          ]}
        />
      </div>

      <form action={BASE_PATH} method="get" className="mt-4 flex flex-wrap items-end gap-3">
        {Object.entries(params).map(([key, value]) =>
          key === "from" || key === "to" || typeof value !== "string" ? null : (
            <input key={key} type="hidden" name={key} value={value} />
          ),
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterFrom}</span>
          <input
            type="date"
            name="from"
            defaultValue={typeof params.from === "string" ? params.from : ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterTo}</span>
          <input
            type="date"
            name="to"
            defaultValue={typeof params.to === "string" ? params.to : ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <button
          type="submit"
          className={buttonVariants({ variant: "outline", size: "xl" })}
        >
          {filters.apply}
        </button>
        <p className="w-full text-[11px] text-n6">{copy.dateRangeHint}</p>

        {appliedFrom || appliedTo ? (
          <p className="tabular w-full text-[11px] font-semibold text-n7">
            {copy.dateRangeApplied}{" "}
            {appliedFrom ? fmt.dateNumeric(instantAt(appliedFrom, "12:00")) : "…"} –{" "}
            {appliedTo ? fmt.dateNumeric(instantAt(appliedTo, "12:00")) : "…"}
          </p>
        ) : null}
      </form>

      <QueryState
        isPending={list.isPending}
        isError={list.isError && !list.data}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
      >
        {canDecide ? (
          <LeaveBulkActions
            selectable={selectable}
            attachmentRequired={attachmentRequired}
            numerals={user.numeralSystem}
          >
            {table}
          </LeaveBulkActions>
        ) : (
          table
        )}
      </QueryState>
    </div>
  );
}
